import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import type { CalibrationAxisKey, CalibrationValues } from '~/features/onboard/calibration';

export const calibrationKeys = {
  current: () => ['me', 'eddy-calibration'] as const,
};

/**
 * FRONT-1406: maps a fetched calibration row back onto the wizard's local
 * `calibration` shape, for the wizard's hydrate-on-mount effect — same
 * pattern as `buildPerfectDayHydration` in `use-perfect-day-spec.ts`
 * (BUG-062). Only the axes actually present in the saved row are included,
 * so `wizardReducer`'s HYDRATE merge leaves any untouched axis alone rather
 * than resetting it to neutral.
 */
export function buildCalibrationHydration(
  values: CalibrationValues | undefined,
): Partial<Record<CalibrationAxisKey, number>> {
  if (!values) return {};
  return { ...values };
}

/**
 * FRONT-1406: the wizard wrote calibration on save (`useSaveCalibration`)
 * but never read it back, so re-entering onboarding always showed neutral
 * sliders — and FRONT-1232's resume positioning lands a returning user
 * directly on this step, so "Finish" then silently wiped the saved axes via
 * `writeCalibration`'s full-replace upsert (see BUG-062's fix invariant 2
 * for why `staleTime: Infinity` matters here — a mid-session background
 * refetch must not clobber sliders the user is currently dragging).
 */
export function useCalibration() {
  return useQuery({
    queryKey: calibrationKeys.current(),
    queryFn: () => api.get<Record<CalibrationAxisKey, number>>('/api/me/eddy-calibration'),
    staleTime: Infinity,
  });
}
